import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import loanGivenService from '../../services/LoanGivenService.js';
import axiosInstance from '../../config/axios.js';
import { Plus, Filter, ArrowLeft, Pencil, Package, MoreVertical, HandCoins, XCircle, Camera} from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const AddLoanGiven = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    borrower_type: '',
    user_id: '',
    borrower_name: '',
    borrower_company: '',
    borrower_contact: '',
    borrower_email: '',
    borrower_address: '',
    loan_type: '',
    loan_amount: '',
    interest_rate: '0',
    loan_date: new Date().toISOString().split('T')[0],
    due_date: '',
    repayment_frequency: '',
    total_installments: '1',
    installment_amount: '0',
    collateral_details: '',
    purpose: '',
    remarks: '',
    auto_approve: false
  });
  
  const [agreementDocument, setAgreementDocument] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [users, setUsers] = useState([]);
  const [loanTypes, setLoanTypes] = useState([]);
  const [borrowerTypes, setBorrowerTypes] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const navigate = useNavigate();

  const { errors, validate, validateField, clearField } = useValidation();

  const validationRules = {
    borrower_type:      (v) => validators.select(v, 'borrower type'),
    user_id:            (v, data) => data.borrower_type === 'employee' ? validators.select(v, 'employee') : '',
    borrower_name:      (v) => validators.required(v, 'Borrower name'),
    borrower_contact:   (v) => validators.required(v, 'Contact') || validators.contact(v),
    borrower_email:     (v) => v ? validators.email(v) : '',
    loan_type:          (v) => validators.select(v, 'loan type'),
    loan_amount:        (v) => validators.required(v, 'Loan amount') || validators.number(v, { min: 0.01, label: 'Loan amount' }),
    loan_date:          (v) => validators.required(v, 'Loan date'),
    due_date:           (v) => validators.required(v, 'Due date'),
    repayment_frequency:(v) => validators.select(v, 'repayment frequency'),
    purpose:            (v) => validators.required(v, 'Purpose'),
    interest_rate:      (v) => v ? validators.number(v, { min: 0, max: 100, label: 'Interest rate' }) : '',
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    calculateTotalAmount();
  }, [formData.loan_amount, formData.interest_rate, formData.loan_date, formData.due_date]);

  useEffect(() => {
    calculateInstallments();
  }, [formData.loan_date, formData.due_date, formData.repayment_frequency, totalAmount]);

  const fetchData = async () => {
    try {
      const [usersRes, typesRes, borrowerTypesRes, freqRes] = await Promise.all([
        axiosInstance.get('/users?all=true&status=active'),
        loanGivenService.getLoanTypes(),
        loanGivenService.getBorrowerTypes(),
        loanGivenService.getFrequencies()
      ]);
      
      setUsers(usersRes.data.data || []);
      setLoanTypes(typesRes.data || []);
      setBorrowerTypes(borrowerTypesRes.data || []);
      setFrequencies(freqRes.data || []);
    } catch (err) {
      Alert.error('Failed to load data');
      console.error(err);
    }
  };

  const calculateTotalAmount = () => {
    const principal = parseFloat(formData.loan_amount) || 0;
    const rate = parseFloat(formData.interest_rate) || 0;
    
    if (principal > 0 && rate > 0 && formData.loan_date && formData.due_date) {
      const startDate = new Date(formData.loan_date);
      const endDate = new Date(formData.due_date);
      const days = Math.max(0, (endDate - startDate) / (1000 * 60 * 60 * 24));
      const interest = (principal * rate * days) / (365 * 100);
      setTotalAmount(principal + interest);
    } else {
      setTotalAmount(principal);
    }
  };

  const calculateInstallments = () => {
    if (!formData.loan_date || !formData.due_date || !formData.repayment_frequency || totalAmount <= 0) {
      return;
    }

    const startDate = new Date(formData.loan_date);
    const endDate = new Date(formData.due_date);
    
    if (endDate <= startDate) {
      return;
    }

    let installments = 0;
    const frequency = formData.repayment_frequency;

    // Calculate number of installments based on frequency
    switch (frequency) {
      case 'daily':
        installments = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
        break;
      case 'weekly':
        installments = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24 * 7));
        break;
      case 'bi_weekly':
        installments = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24 * 14));
        break;
      case 'monthly':
        const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                      (endDate.getMonth() - startDate.getMonth());
        installments = Math.max(1, Math.ceil(months));
        break;
      case 'quarterly':
        const quarters = ((endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                        (endDate.getMonth() - startDate.getMonth())) / 3;
        installments = Math.max(1, Math.ceil(quarters));
        break;
      case 'half_yearly':
        const halfYears = ((endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                          (endDate.getMonth() - startDate.getMonth())) / 6;
        installments = Math.max(1, Math.ceil(halfYears));
        break;
      case 'yearly':
        installments = Math.max(1, Math.ceil(endDate.getFullYear() - startDate.getFullYear()));
        break;
      case 'one_time':
        installments = 1;
        break;
      default:
        installments = 1;
    }

    // Calculate installment amount
    const installmentAmount = installments > 0 ? (totalAmount / installments).toFixed(2) : 0;

    // Update form data
    setFormData(prev => ({
      ...prev,
      total_installments: installments.toString(),
      installment_amount: installmentAmount
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let filtered = type === 'checkbox' ? checked : value;
    if (name === 'borrower_contact') filtered = inputFilters.digits10(value);
    if (name === 'loan_amount' || name === 'interest_rate') filtered = inputFilters.amount(value);

    setFormData(prev => ({
      ...prev,
      [name]: filtered
    }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }

    if (name === 'user_id' && value) {
      const user = users.find(u => u.user_id === value);
      if (user) {
        setFormData(prev => ({
          ...prev,
          borrower_name: user.name,
          borrower_contact: user.contact || '',
          borrower_email: user.email || ''
        }));
        clearField('borrower_name');
        clearField('borrower_contact');
        clearField('borrower_email');
      }
    }
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('File must be less than 5MB');
        return;
      }
      setAgreementDocument(file);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setDocumentPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setDocumentPreview('pdf');
      }
    }
  };

  const removeDocument = () => {
    setAgreementDocument(null);
    setDocumentPreview(null);
    const fileInput = document.getElementById('agreement-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    if (formData.due_date && formData.loan_date && new Date(formData.due_date) <= new Date(formData.loan_date)) {
      Alert.error('Due date must be after loan date');
      return;
    }
    
    setLoading(true);

    try {
      await loanGivenService.create(formData, agreementDocument);
      Alert.success('Loan record created successfully!');
      setTimeout(() => navigate('/loans-given'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create loan');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <DashboardLayout>
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <HandCoins className="w-6 h-6 sm:w-6 sm:h-6 text-buttonBg" />
                    Add Loan Given
                  </h1>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-6 space-y-6 sm:space-y-8">
                  {/* Section 1: Borrower Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Borrower Information
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Borrower Type <span class="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="borrower_type"
                          value={formData.borrower_type}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.borrower_type ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          <option value="">Select Type</option>
                          {borrowerTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.borrower_type} />
                      </div>

                      {formData.borrower_type === 'employee' && (
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Select Employee <span class="text-danger font-bold">*</span>
                          </label>
                          <SelectField variant="form"
                            name="user_id"
                            value={formData.user_id}
                            onChange={handleChange}
                            className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.user_id ? 'border-red-400' : 'border-gray-300'}`}
                            required
                          >
                            <option value="">Select Employee</option>
                            {users.map((user) => (
                              <option key={user.user_id} value={user.user_id}>
                                {user.name} ({user.email})
                              </option>
                            ))}
                          </SelectField>
                          <ValidationMessage error={errors.user_id} />
                        </div>
                      )}

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Borrower Name <span class="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="borrower_name"
                          value={formData.borrower_name}
                          onChange={handleChange}
                          placeholder="Enter borrower name"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.borrower_name ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.borrower_name} />
                      </div>

                      {formData.borrower_type === 'external_company' && (
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Company Name
                          </label>
                          <input
                            type="text"
                            name="borrower_company"
                            value={formData.borrower_company}
                            onChange={handleChange}
                            placeholder="Enter company name"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Contact Number <span class="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="borrower_contact"
                          value={formData.borrower_contact}
                          onChange={handleChange}
                          placeholder="Enter contact number"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.borrower_contact ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.borrower_contact} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          name="borrower_email"
                          value={formData.borrower_email}
                          onChange={handleChange}
                          placeholder="Enter email address"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.borrower_email ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.borrower_email} />
                      </div>

                      <div className="sm:col-span-4">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Address
                        </label>
                        <textarea
                          name="borrower_address"
                          value={formData.borrower_address}
                          onChange={handleChange}
                          placeholder="Enter full address"
                          rows="1"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Loan Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Loan Details
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Type <span class="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="loan_type"
                          value={formData.loan_type}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.loan_type ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          <option value="">Select Type</option>
                          {loanTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.loan_type} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Amount <span class="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="loan_amount"
                          value={formData.loan_amount}
                          onChange={handleChange}
                          placeholder="Enter loan amount"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.loan_amount ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.loan_amount} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Interest Rate (% per annum)
                        </label>
                        <input
                          type="text"
                          name="interest_rate"
                          value={formData.interest_rate}
                          onChange={handleChange}
                          placeholder="Enter interest rate"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.interest_rate ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.interest_rate} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Total Amount (with Interest)
                        </label>
                        <div className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base font-semibold text-green-600">
                          {formatCurrency(totalAmount)}
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Date <span class="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="date"
                          name="loan_date"
                          value={formData.loan_date}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.loan_date ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.loan_date} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Due Date <span class="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="date"
                          name="due_date"
                          value={formData.due_date}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.due_date ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.due_date} />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Repayment Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Repayment Details
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Repayment Frequency <span class="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="repayment_frequency"
                          value={formData.repayment_frequency}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.repayment_frequency ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          <option value="">Select Frequency</option>
                          {frequencies.map((freq) => (
                            <option key={freq.value} value={freq.value}>
                              {freq.label}
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.repayment_frequency} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Total Installments
                        </label>
                        <input
                          type="number"
                          name="total_installments"
                          value={formData.total_installments}
                          readOnly
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base font-semibold text-gray-700"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Installment Amount
                        </label>
                        <input
                          type="text"
                          name="installment_amount"
                          value={formatCurrency(parseFloat(formData.installment_amount) || 0)}
                          readOnly
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base font-semibold text-green-600"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Additional Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Additional Information
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Purpose <span class="text-danger font-bold">*</span>
                        </label>
                        <textarea
                          name="purpose"
                          value={formData.purpose}
                          onChange={handleChange}
                          placeholder="Enter the purpose of the loan"
                          rows="1"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.purpose ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.purpose} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Collateral Details
                        </label>
                        <textarea
                          name="collateral_details"
                          value={formData.collateral_details}
                          onChange={handleChange}
                          placeholder="Enter collateral details (if any)"
                          rows="1"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Agreement Document
                        </label>
                       <input
  type="file"
  id="agreement-input"
  accept="image/*,.pdf"
  onChange={handleDocumentChange}
  className="hidden"
/>

<label
  htmlFor="agreement-input"
  className="w-40 h-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center relative bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
>
  {!documentPreview ? (
    <div className="text-center">
      <Camera className="mx-auto w-8 h-8 text-gray-400 mb-2" />
      <p className="text-xs text-gray-500">Click to upload</p>
    </div>
  ) : documentPreview === 'pdf' ? (
    <div className="text-center text-gray-500 text-xs">
      📄 PDF
    </div>
  ) : (
    <img
      src={documentPreview}
      alt="Document"
      className="w-full h-full object-contain rounded-lg"
    />
  )}

  {documentPreview && (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        removeDocument();
      }}
      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
    >
      ✕
    </button>
  )}
</label>

<p className="text-xs text-gray-600 mt-2">
  Upload agreement document (Max 5MB)
</p>
                        </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Remarks
                        </label>
                        <textarea
                          name="remarks"
                          value={formData.remarks}
                          onChange={handleChange}
                          placeholder="Enter any additional remarks"
                          rows="1"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            name="auto_approve"
                            checked={formData.auto_approve}
                            onChange={handleChange}
                            className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-blue-400"
                          />
                          <span className="text-gray-700">Auto-approve this loan</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <Button type="submit" variant="primary" disabled={loading} loading={loading}>
                      {loading ? 'Creating...' : 'Create Loan'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/loans-given')}>
                      Cancel
                    </Button>
                  </div>
                </form>
    </DashboardLayout>
  );
};

export default AddLoanGiven;